import { ComponentChild, Fragment } from 'preact';
import clsx from 'clsx';
import { ChangeEvent } from 'preact/compat';
import { useCallback, useId } from 'preact/hooks';
import { pluralize } from 'utilities/content';
import { useToggle } from 'utilities/use-toggle';
import { useUniqueId } from 'utilities/use-unique-id';

import { InlineError } from 'components/InlineError';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import styles from './Stars.module.scss';

interface StarsProps {
  label?: string;
  value: number;
  url?: string;
  error?: string;
  connectedRight?: ComponentChild;
  onChange?: (value: number) => void;
}

export function Stars({
  label,
  value,
  url,
  error,
  connectedRight,
  onChange,
}: StarsProps) {
  const uId = useId();
  const id = useUniqueId('Stars');
  const errorId = `${id}Error`;
  const {
    value: focused,
    setTrue: handleFocus,
    setFalse: handleBlur,
  } = useToggle(false);
  const {
    utils: { isRtl },
  } = useSettings();

  const width = value * 20;
  const className = clsx(
    styles.InteractiveContainer,
    focused && styles.focused,
  );

  const labelMarkup = label ? <Text size="small">{label}</Text> : null;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(Number.parseFloat(event.currentTarget.value));
    },
    [onChange],
  );

  const starsMarkup = onChange ? (
    <fieldset
      className={className}
      id={id}
      aria-describedby={error ? errorId : undefined}
    >
      {[...new Array(5)].map((_, index, arr) => {
        const id = `star-radio${uId}${index}`;
        const rating = arr.length - index;
        const title = `${rating} ${pluralize(rating, 'star', 'stars')}`;

        return (
          <Fragment key={id}>
            <input
              id={id}
              type="radio"
              className={styles.Control}
              checked={value === rating}
              value={rating}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <label htmlFor={id} title={title} className={styles.ControlLabel} />
          </Fragment>
        );
      })}
    </fieldset>
  ) : (
    <div className={styles.Stars}>
      <span
        className={clsx(styles.Rating, isRtl && styles.Rtl)}
        style={{ width: `${width}%` }}
      />
    </div>
  );

  const errorMarkup = error ? (
    <InlineError fieldId={id}>{error}</InlineError>
  ) : null;

  const childrenMarkup = (
    <Fragment>
      <Stack vertical spacing="extraTight">
        {labelMarkup}
        <Stack spacing="tight">
          {starsMarkup}
          {connectedRight}
        </Stack>
        {errorMarkup}
      </Stack>
      <span className="wbtx-vh">
        Rating is {value} {pluralize(value, 'star', 'stars')}
      </span>
    </Fragment>
  );

  const wrapperProps = {
    className: styles.Wrapper,
  };

  let wrapperMarkup: JSX.Element;

  if (url) {
    wrapperMarkup = (
      <a href={url} {...wrapperProps}>
        {childrenMarkup}
      </a>
    );
  } else {
    wrapperMarkup = <div {...wrapperProps}>{childrenMarkup}</div>;
  }

  return wrapperMarkup;
}
